import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ModalLayout from '../layouts/ModalLayout';
import Button from '../buttons/Button';
import { Connection } from '../../../redux/slices/connectionsSlice';
import { createOrders } from '../../../services/orders.marketplace.service';
import { PaymentRequestType } from '../../../services/model/marketplace.model';

interface OrdersModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  productId: string;
  productName: string;
  members: Connection[];
}

export default function OrdersModal({
  open,
  setOpen,
  productId,
  productName,
  members,
}: OrdersModalProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedMembers, setSelectedMembers] = useState<Connection[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleMemberToggle = (member: Connection) => {
    setSelectedMembers(prev => {
      const isSelected = prev.some(m => m.id === member.id);
      if (isSelected) {
        return prev.filter(m => m.id !== member.id);
      } 
        return [...prev, member];
      
    });
  };

  const handleSelectAll = () => {
    if (selectedMembers.length === members.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers([...members]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedMembers.length === 0) {
      toast.error(t('error.noMembersSelected'));
      return;
    }

    setIsSubmitting(true);
    
    try {
      await createOrders(selectedMembers.map(member => ({
        customerId: member.userId ?? -1,
        productId,
        paymentRequestType: PaymentRequestType.SC_DIRECT_DEBIT,
      })))
      .then(() => {
        toast.success(t('success.ordersCreated'));
      })
      .catch(() => {
        toast.error(t('error.ordersCreationFailed'));
      });

      setSelectedMembers([]);
      setOpen(false);
    } catch (error) {
      console.error('Error creating orders:', error);
      toast.error(t('error.ordersCreationFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedMembers([]);
    setOpen(false);
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 w-full max-w-lg rounded-[20px] bg-secondary-50 p-6"
    >
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-primary-900">
          Send payment requests for &quot;{productName}&quot;
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          Select the members you want to send payment requests to
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">
            Select members ({selectedMembers.length}/{members.length})
          </label>
          <Button
            type="button"
            variant="secondary"
            onClick={handleSelectAll}
            className="text-sm"
          >
            {selectedMembers.length === members.length ? 'Deselect all' : 'Select all'}
          </Button>
        </div>

        <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200">
          {members.map((member) => {
            const isSelected = selectedMembers.some(m => m.id === member.id);
            return (
              <div
                key={member.id}
                className={`flex items-center p-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                  isSelected ? 'bg-primary-50 border-primary-200' : ''
                }`}
                onClick={() => handleMemberToggle(member)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleMemberToggle(member)}
                  className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {member.fields.FIRST_NAME} {member.fields.LAST_NAME}
                  </p>
                  {member.fields.EMAIL && (
                    <p className="text-sm text-gray-500">{member.fields.EMAIL}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {members.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No members available to send payment requests to.</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || selectedMembers.length === 0}
          >
            {isSubmitting ? 'Sending...' : `Send ${selectedMembers.length} payment request${selectedMembers.length !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </form>
    </ModalLayout>
  );
}
